/* eslint-disable */

import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { format, startOfDay } from 'date-fns';
import * as yup from 'yup';
import DatePicker from 'react-datepicker';

// @ts-ignore
import 'react-datepicker/dist/react-datepicker.css';

import { CommonDropdown, CommonInput, FormErrorWrap, RegularButton } from 'pg-ui-library';

import { Container, TabContainer, Tab, TabTitle, Content, ModuleLine, FormInputWrapper, Heading } from './styles';

import { useQuery } from '@tanstack/react-query';
import LeavepageService from 'src/services/leave-page.service';
import { useSubmitLeave, useReSubmitLeave } from './mutation/add-leave-mutation';

import { DefaultTable } from 'src/components/default-table';
import { TableColumn } from 'react-data-table-component';
import EmployeeTable from './leave-form/leaveapplication-table';
import { WithdrawModal } from './../my-leave/withdraw-modal';

const HOURS_PER_FULL_DAY = 8;

const FIRST_HALF = 'First Half';
const SECOND_HALF = 'Second Half';
const FULL_DAY = 'Full Day';

type AttachmentItem =
  | {
      id: string;
      attachmentUrl: string;
      attachmentName: string;
      type: 'existing';
    }
  | {
      file: File;
      type: 'new';
      name: string;
    };

/* =========================================================
   LEAVE BALANCE CARD
========================================================= */

const LeaveBalanceCard = ({ leaveBalances }: { leaveBalances?: any[] }) => {
  if (!leaveBalances || leaveBalances.length === 0) {
    return null;
  }

  const otherLeaves = leaveBalances.filter((item: any) => !['CL', 'SL'].includes(item.accrualId));

  const columns: TableColumn<any>[] = [
    {
      name: 'Leave Type',
      selector: (row: any) => row.accrualId ?? 0,
    },
    {
      name: 'Total Accrued',
      selector: (row: any) => row.totalAccruedHours ?? 0,
      cell: (row: any) => Number(row.totalAccruedHours ?? 0).toFixed(1),
    },
    {
      name: 'Hours Taken',
      selector: (row: any) => row.approvedLeaveHours ?? 0,
    },
    {
      name: 'Pending Approval',
      selector: (row: any) => row.pendingLeaveHours ?? 0,
    },
    {
      name: 'Available Balance',
      selector: (row: any) => Number(row.totalAvailableHours ?? 0).toFixed(1),
    },
  ];

  return (
    <div
      style={{
        width: '100%',
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
        padding: '24px',
        border: '1px solid #eaeaea',
        boxSizing: 'border-box',
      }}
    >
      <h4
        style={{
          fontSize: '26px',
          marginBottom: '20px',
          color: '#333',
          fontWeight: '600',
        }}
      >
        Leave Balance(In Hours)
      </h4>

      <DefaultTable columns={columns} data={otherLeaves} pagination={false} />
    </div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const LeavePage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState('leaveForm');

  const [startDateFn, setStartDateFn] = useState<Date | null>(null);

  const [endDateFn, setEndDateFn] = useState<Date | null>(null);

  const [selectedLeaveBalance, setSelectedLeaveBalance] = useState<number | null>(null);

  const [lengthDays, setLengthDays] = useState<any>(0);

  const [lengthHours, setLengthHours] = useState<any>('');

  const [isManualEdit, setIsManualEdit] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [isEdit, setIsEdit] = useState(false);

  const [reSubmitleaveId, setReSubmitleaveId] = useState('');

  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [leaveTypeOptions, setLeaveTypeOptions] = useState<
    {
      label: string;
      value: string;
      key: string;
      description: string;
    }[]
  >([]);

  const [selectedLeaveDescription, setSelectedLeaveDescription] = useState<string | null>(null);

  const submitLeave = useSubmitLeave();
  const resubmitLeave = useReSubmitLeave();

  /* =========================================================
     VALIDATION
  ========================================================= */

  const validationSchema = yup.object({
    startDate: yup.string().trim().required('Start date is required'),

    endDate: yup
      .string()
      .trim()
      .required('End date is required')
      .test('is-after-start-date', 'End date cannot be before start date', function (value) {
        const { startDate } = this.parent;

        if (!startDate || !value) {
          return true;
        }

        const start = new Date(`${startDate}T00:00:00`);
        const end = new Date(`${value}T00:00:00`);

        return end >= start;
      }),

    length: yup.number().required('Day is required').min(0, 'Day cannot be negative'),

    startSession: yup
      .string()
      .oneOf([FIRST_HALF, SECOND_HALF, FULL_DAY], 'Invalid start session')
      .required('Start session is required'),

    endSession: yup
      .string()
      .oneOf([FIRST_HALF, SECOND_HALF, FULL_DAY], 'Invalid end session')
      .required('End session is required'),

    accrualName: yup.string().required('Leave type is required'),

    attachments: yup.array().optional(),

    // Only whole numbers: 0, 1, 2, 10, 100...
    // Not allowed: -1, 1.5, abc, 1e3
    lengthHours: yup
      .string()
      .required('Hours duration is required.')
      .matches(/^\d+$/, 'Enter a valid whole number.')
      .test(
        'non-negative',
        'Hours duration must be greater than or equal to 0.',
        (value: string | undefined) => value === undefined || Number(value) >= 0
      ),
  });
  /* =========================================================
     FORMIK
  ========================================================= */

  const formik = useFormik({
    initialValues: {
      startDate: '',
      endDate: '',
      length: 0,

      /*
       * IMPORTANT:
       * Hours starts BLANK.
       */
      hours: '',

      accrualName: '',
      reason: '',

      startSession: FIRST_HALF,
      endSession: SECOND_HALF,

      attachments: [] as AttachmentItem[],

      /*
       * IMPORTANT:
       * Hours Duration starts BLANK.
       */
      lengthHours: '',
    },

    validationSchema,

    onSubmit: async (values: any) => {
      console.log('Form submitted with values:', values);

      /* =====================================================
         REQUESTED HOURS
      ===================================================== */

      const requestedHours = Number(values.lengthHours);

      /* =====================================================
         AVAILABLE LEAVE BALANCE
      ===================================================== */

      const availableBalance = Number(selectedLeaveBalance ?? 0);

      console.log('Requested Hours:', requestedHours);

      console.log('Available Leave Balance:', availableBalance);

      /* =====================================================
         VALIDATE HOURS
      ===================================================== */

      if (values.lengthHours === '' || values.lengthHours === null || values.lengthHours === undefined) {
        alert('Please enter the required number of leave hours.');

        return;
      }

      if (Number.isNaN(requestedHours)) {
        alert('Please enter a valid number of leave hours.');

        return;
      }

      if (requestedHours <= 0) {
        alert('Leave hours must be greater than 0.');

        return;
      }

      /* =====================================================
         CHECK LEAVE BALANCE

         CONDITION:
         Requested Hours <= Leave Balance
         = ALLOW

         Requested Hours > Leave Balance
         = BLOCK
      ===================================================== */

      if (requestedHours > availableBalance) {
        alert(
          `You cannot submit this leave request.\n\n` +
            `Requested Hours: ${requestedHours}\n` +
            `Available Leave Balance: ${availableBalance}\n\n` +
            `Please enter hours less than or equal to your available leave balance.`
        );

        return;
      }

      /* =====================================================
         NORMAL SUBMISSION
      ===================================================== */

      try {
        const leaveData = new FormData();

        leaveData.append('length', values.length.toString());

        leaveData.append('hours', values.lengthHours.toString());

        leaveData.append('accrualName', values.accrualName);

        leaveData.append('reason', values.reason);

        leaveData.append('endDate', values.endDate);

        leaveData.append('startDate', values.startDate);

        leaveData.append('endSession', values.endSession);

        leaveData.append('startSession', values.startSession);

        /* ===================================================
           ATTACHMENTS
        =================================================== */

        const existingAttachmentIds: string[] = [];

        const newFiles: File[] = [];

        values.attachments.forEach((attachment: AttachmentItem) => {
          if (attachment.type === 'existing') {
            existingAttachmentIds.push(attachment.id);
          } else if (attachment.type === 'new') {
            newFiles.push(attachment.file);
          }
        });

        leaveData.append('existingAttachmentIds', JSON.stringify(existingAttachmentIds));

        leaveData.append('deletedAttachmentIds', JSON.stringify(deletedAttachmentIds));

        newFiles.forEach(file => {
          leaveData.append('attachments', file);
        });

        /* ===================================================
           EDIT / NEW REQUEST
        =================================================== */

        if (isEdit) {
          await resubmitLeave.mutateAsync({
            leaveId: reSubmitleaveId,
            leave: leaveData,
          });
        } else {
          await submitLeave.mutateAsync({
            leave: leaveData,
          });
        }

        /* ===================================================
           RESET FORM
        =================================================== */

        formik.resetForm();

        setStartDateFn(null);
        setEndDateFn(null);

        setLengthDays(0);
        setLengthHours('');

        setSelectedLeaveBalance(null);

        setSelectedLeaveDescription(null);

        setDeletedAttachmentIds([]);

        setIsEdit(false);

        setReSubmitleaveId('');

        setIsManualEdit(false);
      } catch (error: any) {
        console.error('Submit error:', error);

        console.error('Error response:', error?.response?.data);

        alert(error?.response?.data?.message || 'Failed to submit leave request');
      }
    },
  });

  /* =========================================================
     RESET WHEN MOVING TO MY LEAVES TAB
  ========================================================= */

  useEffect(() => {
    if (selectedTab === 'leaveApply') {
      if (isEdit) {
        setIsEdit(false);
        setReSubmitleaveId('');
      }

      formik.resetForm();

      setStartDateFn(null);
      setEndDateFn(null);

      setLengthDays(0);
      setLengthHours('');

      setSelectedLeaveBalance(null);

      setSelectedLeaveDescription(null);

      setDeletedAttachmentIds([]);

      setIsManualEdit(false);
    }
  }, [selectedTab]);

  /* =========================================================
     MAP SESSION
  ========================================================= */

  const mapSession = (session: string) => {
    switch (session) {
      case 'FIRST_HALF':
      case 'First Half':
        return FIRST_HALF;

      case 'SECOND_HALF':
      case 'Second Half':
        return SECOND_HALF;

      case 'FULL_DAY':
      case 'Full Day':
        return FULL_DAY;

      default:
        return FIRST_HALF;
    }
  };

  /* =========================================================
     EDIT EXISTING LEAVE
  ========================================================= */

  const handleEdit = (leaveData: any) => {
    console.log('handleEdit received leaveData:', leaveData);

    setIsManualEdit(true);

    setIsEdit(true);

    setReSubmitleaveId(leaveData.id);

    setDeletedAttachmentIds([]);

    /* =====================================================
       ATTACHMENTS
    ===================================================== */

    const currentNewAttachments = formik.values.attachments.filter((att: AttachmentItem) => att.type === 'new');

    const serverAttachments = (leaveData.attachments || []).map((attachment: any) => ({
      id: attachment.id,
      attachmentUrl: attachment.attachmentUrl,
      attachmentName: attachment.attachmentName || 'Attachment',
      type: 'existing' as const,
    }));

    const combinedAttachments = [...currentNewAttachments, ...serverAttachments];

    formik.setFieldValue('attachments', combinedAttachments);

    /* =====================================================
       FORM VALUES
    ===================================================== */

    formik.setFieldValue('startDate', leaveData.startDate);

    formik.setFieldValue('endDate', leaveData.endDate);

    formik.setFieldValue('accrualName', leaveData.accrualName);

    formik.setFieldValue('reason', leaveData.reason);

    formik.setFieldValue('startSession', mapSession(leaveData.startSession));

    formik.setFieldValue('endSession', mapSession(leaveData.endSession));

    /* =====================================================
       EXISTING REQUESTED HOURS

       When editing, keep the hours that
       were previously entered.
    ===================================================== */

    const savedHours = Number(leaveData.lengthHours) || Number(leaveData.hours) || 0;

    const savedDays = Number(leaveData.length) || Number((savedHours / HOURS_PER_FULL_DAY).toFixed(1)) || 0;

    setLengthHours(savedHours);

    setLengthDays(savedDays);

    formik.setFieldValue('hours', savedHours);

    formik.setFieldValue('lengthHours', savedHours);

    formik.setFieldValue('length', savedDays);

    /* =====================================================
       DATES
    ===================================================== */

    setStartDateFn(new Date(leaveData.startDate));

    setEndDateFn(new Date(leaveData.endDate));

    /* =====================================================
       LEAVE DESCRIPTION
    ===================================================== */

    const selectedOption = leaveTypeOptions.find(option => option.value === leaveData.accrualName);

    setSelectedLeaveDescription(selectedOption?.description || null);

    /* =====================================================
       LEAVE BALANCE
    ===================================================== */

    const selectedBalance = leaveBalances?.find((balance: any) => balance.accrualId === leaveData.accrualName);

    setSelectedLeaveBalance(Number(selectedBalance?.totalAvailableHours ?? 0));

    setSelectedTab('leaveForm');
  };

  /* =========================================================
     LEAVE BALANCE API
  ========================================================= */

  const {
    data: leaveBalances,
    isLoading,
    error,
  } = useQuery(
    ['leave-balance'],
    async () => {
      const response = await LeavepageService.getLeaveBalance();

      return response.data;
    },
    {
      onSuccess: (data: any[]) => {
        const options = data.map((item: any) => ({
          label: item.description,
          value: item.accrualId,
          key: item.accrualId,
          description: item.description,
        }));

        setLeaveTypeOptions(options);
      },

      onError: (err: any) => {
        console.error('Failed to load leave types:', err);
      },
    }
  );

  /* =========================================================
     FILE UPLOAD
  ========================================================= */

  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    const newAttachments: AttachmentItem[] = Array.from(files).map(file => ({
      file,
      type: 'new',
      name: file.name,
    }));

    formik.setFieldValue('attachments', [...formik.values.attachments, ...newAttachments]);

    /*
     * Allow selecting the same file again
     * after removing it.
     */
    event.target.value = '';
  };

  /* =========================================================
     REMOVE ATTACHMENT
  ========================================================= */

  const handleRemoveAttachment = (index: number) => {
    const removedAttachment = formik.values.attachments[index];

    if (removedAttachment.type === 'existing') {
      setDeletedAttachmentIds(prev => [...prev, removedAttachment.id]);
    }

    const updatedAttachments = formik.values.attachments.filter((_: any, i: number) => i !== index);

    formik.setFieldValue('attachments', updatedAttachments);
  };

  /* =========================================================
     LEAVE TYPE CHANGE

     When employee changes leave type:
     - Load balance
     - Clear previously entered hours
  ========================================================= */

  const handleLeaveTypeChange = (value: string) => {
    formik.setFieldValue('accrualName', value);

    const selectedOption = leaveTypeOptions.find(option => option.value === value);

    setSelectedLeaveDescription(selectedOption?.description || null);

    const selectedBalance = leaveBalances?.find((balance: any) => balance.accrualId === value);

    const availableBalance = Number(selectedBalance?.totalAvailableHours ?? 0);

    setSelectedLeaveBalance(availableBalance);

    /*
     * Clear old hours when
     * leave type changes.
     */
    formik.setFieldValue('lengthHours', '');

    formik.setFieldValue('hours', '');

    formik.setFieldValue('length', 0);

    setLengthHours('');
    setLengthDays(0);

    setIsManualEdit(false);
  };

  /* =========================================================
     MANUAL HOURS CHANGE

     Employee manually enters hours.
     No automatic calculation from dates.
  ========================================================= */

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    /* =====================================================
       ALLOW EMPTY VALUE
    ===================================================== */

    if (value === '') {
      setIsManualEdit(true);

      setLengthHours('');
      setLengthDays(0);

      formik.setFieldValue('lengthHours', '', false);

      formik.setFieldValue('hours', '', false);

      formik.setFieldValue('length', 0, false);

      return;
    }

    const hours = Number(value);

    /* =====================================================
       INVALID NUMBER
    ===================================================== */

    if (Number.isNaN(hours) || hours < 0) {
      return;
    }

    setIsManualEdit(true);

    /*
     * Convert entered hours to days
     * only for the length field sent
     * to the backend.
     */
    const calculatedDays = Number((hours / HOURS_PER_FULL_DAY).toFixed(1));

    setLengthHours(hours);

    setLengthDays(calculatedDays);

    formik.setFieldValue('lengthHours', hours, false);

    formik.setFieldValue('hours', hours, false);

    formik.setFieldValue('length', calculatedDays, false);

    formik.setFieldTouched('lengthHours', true);

    setTimeout(() => {
      formik.validateField('lengthHours');
    }, 0);
  };

  /* =========================================================
     HOURS BALANCE ERROR
  ========================================================= */

  const getHoursBalanceError = () => {
    const enteredHours = Number(formik.values.lengthHours);

    const availableHours = Number(selectedLeaveBalance ?? 0);

    if (formik.values.lengthHours === '') {
      return null;
    }

    if (Number.isNaN(enteredHours)) {
      return 'Please enter a valid number of hours.';
    }

    if (enteredHours > availableHours) {
      return (
        `Entered hours (${enteredHours}) ` + `exceed your available leave balance ` + `(${availableHours.toFixed(2)}).`
      );
    }

    return null;
  };

  /* =========================================================
     HANDLE SUBMIT CLICK

     This performs:
     1. Form validation
     2. Hours validation
     3. Leave balance validation
     4. API submission
  ========================================================= */

  const handleSubmitClick = async () => {
    console.log('Submit clicked');

    console.log('DatePicker startDateFn:', startDateFn);

    console.log('DatePicker endDateFn:', endDateFn);

    console.log('Formik startDate:', formik.values.startDate);

    console.log('Formik endDate:', formik.values.endDate);

    // Safety sync between DatePicker and Formik
    const formattedStartDate = startDateFn ? format(startOfDay(startDateFn), 'yyyy-MM-dd') : '';

    const formattedEndDate = endDateFn ? format(startOfDay(endDateFn), 'yyyy-MM-dd') : '';

    formik.setFieldValue('startDate', formattedStartDate, false);

    formik.setFieldValue('endDate', formattedEndDate, false);

    formik.setFieldTouched('startDate', true, false);

    formik.setFieldTouched('endDate', true, false);

    formik.setFieldTouched('accrualName', true, false);

    formik.setFieldTouched('lengthHours', true, false);

    // Validate using the actual current values
    const valuesToValidate = {
      ...formik.values,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };

    try {
      await validationSchema.validate(valuesToValidate, {
        abortEarly: false,
      });
    } catch (validationError: any) {
      const validationErrors: Record<string, string> = {};

      if (validationError?.inner && validationError.inner.length > 0) {
        validationError.inner.forEach((error: any) => {
          if (error.path && !validationErrors[error.path]) {
            validationErrors[error.path] = error.message;
          }
        });
      }

      console.log('Validation errors:', validationErrors);

      Object.entries(validationErrors).forEach(([field, message]) => {
        formik.setFieldError(field, message);
      });

      return;
    }

    const requestedHours = Number(formik.values.lengthHours);

    const availableBalance = Number(selectedLeaveBalance ?? 0);

    if (formik.values.lengthHours === '' || Number.isNaN(requestedHours)) {
      formik.setFieldTouched('lengthHours', true);

      formik.setFieldError('lengthHours', 'Please enter the required number of hours');

      return;
    }

    if (requestedHours <= 0) {
      formik.setFieldError('lengthHours', 'Leave hours must be greater than 0');

      return;
    }

    if (requestedHours > availableBalance) {
      alert(
        `You cannot submit this leave request.\n\n` +
          `Requested Hours: ${requestedHours}\n` +
          `Available Leave Balance: ${availableBalance}\n\n` +
          `Please enter hours less than or equal to your available leave balance.`
      );

      return;
    }

    // Everything is valid
    await formik.submitForm();
  };

  /* =========================================================
     HANDLE RE-SUBMIT CLICK
  ========================================================= */

  const handleResubmitClick = async () => {
    console.log('Re-Submit clicked');

    console.log('Form values:', formik.values);

    formik.setFieldTouched('startDate', true);

    formik.setFieldTouched('endDate', true);

    formik.setFieldTouched('accrualName', true);

    formik.setFieldTouched('lengthHours', true);

    const errors = await formik.validateForm();

    console.log('Form errors:', errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const requestedHours = Number(formik.values.lengthHours);

    const availableBalance = Number(selectedLeaveBalance ?? 0);

    /* =====================================================
         HOURS REQUIRED
      ===================================================== */

    if (formik.values.lengthHours === '' || Number.isNaN(requestedHours)) {
      alert('Please enter the required number of leave hours.');

      return;
    }

    if (requestedHours <= 0) {
      alert('Leave hours must be greater than 0.');

      return;
    }

    /* =====================================================
         LEAVE BALANCE CHECK
      ===================================================== */

    if (requestedHours > availableBalance) {
      alert(
        `You cannot re-submit this leave request.\n\n` +
          `Requested Hours: ${requestedHours}\n` +
          `Available Leave Balance: ${availableBalance}\n\n` +
          `Please enter hours less than or equal to your available leave balance.`
      );

      return;
    }

    /*
     * Valid.
     */
    await formik.submitForm();
  };

  /* =========================================================
     RENDER TAB CONTENT
  ========================================================= */

  const renderTabContent = () => {
    /* =====================================================
         NEW LEAVE FORM
      ===================================================== */

    if (selectedTab === 'leaveForm') {
      return (
        <div
          style={{
            width: '100%',
            maxWidth: '1200px',
            margin: '0',
            padding: '0 16px',
            marginTop: '24px',
          }}
        >
          {/* =================================================
                LEAVE BALANCE CARD
            ================================================= */}

          <div
            style={{
              marginBottom: '24px',
              width: '100%',
            }}
          >
            <LeaveBalanceCard leaveBalances={leaveBalances} />
          </div>

          {/* =================================================
                LEAVE FORM
            ================================================= */}

          <div
            style={{
              width: '100%',
            }}
          >
            <form onSubmit={formik.handleSubmit}>
              {/* =================================================
                    LEAVE TYPE
                ================================================= */}

              <ModuleLine>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    width: '100%',
                  }}
                >
                  {/* Leave Name */}

                  <FormInputWrapper
                    style={{
                      flex: 1,
                      minWidth: '200px',
                    }}
                  >
                    {isLoading ? (
                      <div
                        style={{
                          color: 'gray',
                        }}
                      >
                        Loading leave types...
                      </div>
                    ) : error ? (
                      <div
                        style={{
                          color: 'red',
                        }}
                      >
                        Failed to load leave types.
                      </div>
                    ) : (
                      <>
                        <label>Leave Name*</label>

                        <div
                          style={{
                            width: '100%',
                          }}
                        >
                          <CommonDropdown
                            label=""
                            id="accrualName"
                            name="accrualName"
                            options={[
                              {
                                label: 'Select from dropdown',
                                value: '',
                                key: 'placeholder',
                                description: '',
                              },
                              ...leaveTypeOptions,
                            ]}
                            value={formik.values.accrualName}
                            setValue={(value: string) => handleLeaveTypeChange(value)}
                            wrapperStyle={{
                              width: '100%',
                              maxWidth: '100%',
                              flex: 1,
                            }}
                            selectStyle={{
                              width: '100%',
                              minWidth: '100%',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        {formik.touched.accrualName && formik.errors.accrualName && (
                          <div
                            style={{
                              color: 'red',
                              marginTop: '4px',
                            }}
                          >
                            {formik.errors.accrualName}
                          </div>
                        )}
                      </>
                    )}
                  </FormInputWrapper>

                  {/* Leave Balance */}

                  <FormInputWrapper
                    style={{
                      flex: 1,
                      minWidth: '200px',
                    }}
                  >
                    <label>Leave Balance Hours</label>

                    <CommonInput
                      value={Number(selectedLeaveBalance ?? 0).toFixed(1)}
                      readOnly
                      label=""
                      style={{
                        width: '100%',
                      }}
                    />
                  </FormInputWrapper>
                </div>
              </ModuleLine>

              {/* =================================================
                    DATE PICKERS
                ================================================= */}

              <ModuleLine
                style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                {/* Start Date */}

                <FormInputWrapper
                  style={{
                    flex: '1 1 calc(50% - 8px)',
                    minWidth: '220px',
                  }}
                >
                  <label>Leave Start Date*</label>

                  <DatePicker
                    selected={startDateFn}
                    onChange={(date: Date | null) => {
                      if (!date) {
                        setStartDateFn(null);

                        formik.setFieldValue('startDate', '', false);

                        return;
                      }

                      const localDate = startOfDay(date);

                      const formattedDate = format(localDate, 'yyyy-MM-dd');

                      console.log('START DATE SELECTED:', formattedDate);

                      // DatePicker state
                      // DatePicker state
                      setStartDateFn(localDate);

                      // Formik state
                      formik.setFieldValue('startDate', formattedDate, false);

                      // Mark as touched AFTER setting the value
                      formik.setFieldTouched('startDate', true, false);

                      // Clear any stale manual error now that a valid date is selected
                      if (formik.errors.startDate) {
                        formik.setFieldError('startDate', undefined);
                      }

                      // If selected start date is after current end date,
                      // clear the end date.
                      if (endDateFn && localDate > endDateFn) {
                        setEndDateFn(null);

                        formik.setFieldValue('endDate', '', false);
                      }
                    }}
                    dateFormat="yyyy-MM-dd"
                    minDate={(() => {
                      const today = new Date();

                      return new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
                    })()}
                    placeholderText="Select Start Date"
                  />

                  {formik.touched.startDate && formik.errors.startDate && (
                    <div
                      style={{
                        color: 'red',
                        marginTop: '4px',
                      }}
                    >
                      {formik.errors.startDate}
                    </div>
                  )}
                </FormInputWrapper>

                {/* End Date */}

                <FormInputWrapper
                  style={{
                    flex: '1 1 calc(50% - 8px)',
                    minWidth: '220px',
                  }}
                >
                  <label>Leave End Date*</label>

                  <DatePicker
                    selected={endDateFn}
                    onChange={(date: Date | null) => {
                      if (!date) {
                        setEndDateFn(null);

                        formik.setFieldValue('endDate', '', false);

                        return;
                      }

                      const localDate = startOfDay(date);

                      const formattedDate = format(localDate, 'yyyy-MM-dd');

                      console.log('END DATE SELECTED:', formattedDate);

                      // DatePicker state
                      setEndDateFn(localDate);

                      // Formik state
                      formik.setFieldValue('endDate', formattedDate, false);

                      // Mark as touched
                      formik.setFieldTouched('endDate', true, false);

                      // Clear any stale manual error now that a valid date is selected
                      if (formik.errors.endDate) {
                        formik.setFieldError('endDate', undefined);
                      }
                    }}
                    dateFormat="yyyy-MM-dd"
                    minDate={startDateFn || undefined}
                    placeholderText="Select End Date"
                  />

                  {formik.touched.endDate && formik.errors.endDate && (
                    <div
                      style={{
                        color: 'red',
                        marginTop: '4px',
                      }}
                    >
                      {formik.errors.endDate}
                    </div>
                  )}
                </FormInputWrapper>
              </ModuleLine>

              {/* =================================================
                    HOURS DURATION
                ================================================= */}

              <ModuleLine>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    width: '100%',
                  }}
                >
                  <FormInputWrapper
                    style={{
                      flex: '1 1 50%',
                      minWidth: '0',
                    }}
                  >
                    <label>Hours Duration*</label>

                    <CommonInput
                      type="text"
                      inputMode="numeric"
                      value={formik.values.lengthHours === '' ? '' : String(formik.values.lengthHours)}
                      label=""
                      placeholder="Enter required hours"
                      onChange={handleHoursChange}
                      onBlur={() => {
                        formik.setFieldTouched('lengthHours', true);
                        formik.validateField('lengthHours');
                      }}
                      style={{ width: '100%' }}
                    />

                    {/* Yup validation error */}

                    {formik.touched.lengthHours && formik.errors.lengthHours && (
                      <div
                        style={{
                          color: 'red',
                          marginTop: '4px',
                        }}
                      >
                        {formik.errors.lengthHours}
                      </div>
                    )}

                    {/* Leave balance error */}

                    {!formik.errors.lengthHours &&
                      formik.values.lengthHours !== '' &&
                      Number(formik.values.lengthHours) > Number(selectedLeaveBalance ?? 0) && (
                        <div
                          style={{
                            color: 'red',
                            marginTop: '4px',
                          }}
                        >
                          Entered hours ({formik.values.lengthHours}) exceed your available leave balance (
                          {Number(selectedLeaveBalance ?? 0).toFixed(2)}
                          ).
                        </div>
                      )}
                  </FormInputWrapper>
                </div>
              </ModuleLine>

              {/* =================================================
                    REASON + ATTACHMENTS
                ================================================= */}

              <ModuleLine>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '20px',
                    flexWrap: 'wrap',
                    width: '100%',
                  }}
                >
                  {/* Reason */}

                  <FormInputWrapper
                    style={{
                      flex: '1 1 100%',
                      minWidth: '0',
                    }}
                  >
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '5px',
                      }}
                    >
                      Reason for Leave
                    </label>

                    <textarea
                      id="reason"
                      name="reason"
                      placeholder="Enter the reason for leave"
                      value={formik.values.reason}
                      onChange={event => formik.setFieldValue('reason', event.target.value)}
                      style={{
                        width: '100%',
                        height: '80px',
                        borderRadius: '7px',
                        border: '1px solid #ccc',
                        padding: '10px',
                        boxSizing: 'border-box',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                      }}
                    />

                    <FormErrorWrap errorMessage={formik.errors.reason as string} visible={!!formik.errors.reason} />
                  </FormInputWrapper>

                  {/* Attachments */}

                  <FormInputWrapper
                    style={{
                      flex: '1 1 0',
                      minWidth: 0,
                    }}
                  >
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '5px',
                      }}
                    >
                      Attachments
                    </label>

                    <input
                      ref={fileInputRef}
                      id="fileUpload"
                      name="Attachment"
                      type="file"
                      multiple
                      style={{
                        display: 'none',
                      }}
                      onChange={handleFileChange}
                    />

                    <div
                      style={{
                        width: '100%',
                        height: '80px',
                        border: '1px dashed #ccc',
                        borderRadius: '7px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        marginBottom: '10px',
                        boxSizing: 'border-box',
                        backgroundColor: '#fafafa',
                      }}
                      onClick={handleFileUploadClick}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '14px',
                        }}
                      >
                        <span
                          style={{
                            color: '#4A4BA9',
                            fontWeight: 'bold',
                            marginRight: '5px',
                          }}
                        >
                          + Add files
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: '5px',
                      }}
                    >
                      {formik.values.attachments.length > 0 ? (
                        formik.values.attachments.map((attachment: AttachmentItem, index: number) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '6px',
                              padding: '8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0',
                            }}
                          >
                            <a
                              href={
                                attachment.type === 'existing'
                                  ? attachment.attachmentUrl
                                  : URL.createObjectURL(attachment.file)
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#4A4BA9',
                                textDecoration: 'none',
                                fontSize: '13px',
                                marginRight: '10px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}
                            >
                              {attachment.type === 'existing' ? attachment.attachmentName : attachment.name}
                            </a>

                            <span
                              onClick={() => handleRemoveAttachment(index)}
                              style={{
                                color: '#ff4444',
                                cursor: 'pointer',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                marginLeft: '8px',
                                lineHeight: 1,
                              }}
                              title="Remove"
                            >
                              ×
                            </span>
                          </div>
                        ))
                      ) : (
                        <span
                          style={{
                            color: '#999',
                            fontSize: '12px',
                          }}
                        >
                          No attachments added
                        </span>
                      )}
                    </div>
                  </FormInputWrapper>
                </div>
              </ModuleLine>

              {/* =================================================
                    BUTTONS
                ================================================= */}

              {isEdit ? (
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginTop: '24px',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* RE-SUBMIT */}

                  <RegularButton
                    type="button"
                    onClick={handleResubmitClick}
                    style={{
                      flex: '1 1 auto',
                      maxWidth: '150px',
                      minWidth: '110px',
                      padding: '12px 20px',
                    }}
                    disabled={
                      resubmitLeave.isLoading ||
                      formik.values.lengthHours === '' ||
                      Number(formik.values.lengthHours) <= 0
                    }
                  >
                    Re-Submit
                  </RegularButton>

                  {/* WITHDRAW */}

                  <RegularButton
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    style={{
                      flex: '1 1 auto',
                      maxWidth: '150px',
                      minWidth: '110px',
                      padding: '12px 20px',
                    }}
                    disabled={resubmitLeave.isLoading}
                  >
                    Withdraw
                  </RegularButton>
                </div>
              ) : (
                /* SUBMIT */

                <RegularButton
                  type="button"
                  onClick={handleSubmitClick}
                  style={{
                    marginTop: '24px',
                    padding: '12px 32px',
                    minWidth: '130px',
                  }}
                  disabled={
                    submitLeave.isLoading || formik.values.lengthHours === '' || Number(formik.values.lengthHours) <= 0
                  }
                >
                  Submit
                </RegularButton>
              )}
            </form>
          </div>
        </div>
      );
    }

    /* =======================================================
         MY LEAVES TAB
      ======================================================= */

    if (selectedTab === 'leaveApply') {
      return (
        <div
          style={{
            width: '100%',
            maxWidth: '1500px',
            margin: '10px',
            padding: '0 16px',
            marginTop: '15px',
          }}
        >
          <ModuleLine>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: '20px',
                  color: '#1a1a32',
                }}
              >
                My Leaves
              </h3>
            </div>
          </ModuleLine>

          <div
            style={{
              width: '100%',
              overflowX: 'auto',
              marginTop: '16px',
            }}
          >
            <EmployeeTable onEdit={leaveData => handleEdit(leaveData)} />
          </div>
        </div>
      );
    }

    return null;
  };

  /* =========================================================
     MAIN RETURN
  ========================================================= */

  return (
    <>
      <Container>
        <Heading>
          <h3>My Leaves</h3>
        </Heading>

        <TabContainer>
          {['leaveForm', 'leaveApply'].map(tab => (
            <Tab key={tab} onClick={() => setSelectedTab(tab)} selected={tab === selectedTab}>
              <TabTitle>{tab === 'leaveForm' ? 'New Leave Request' : 'My Leaves'}</TabTitle>
            </Tab>
          ))}
        </TabContainer>

        {renderTabContent()}

        {/* =====================================================
            WITHDRAW MODAL
        ===================================================== */}

        <WithdrawModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          leaveId={reSubmitleaveId}
          onClose={() => setIsModalOpen(false)}
          onConfirm={() => setIsModalOpen(false)}
        />
      </Container>
    </>
  );
};

export default LeavePage;
